import { pcbLayoutRootNodeId } from "@buildwithflux/models";
/**
 * Removes orphaned (root disconnected) subtrees from the input tree of PCB nodes.
 *
 * It works by:
 * - iterating each node of the tree,
 * - traversing to the root (if possible)
 * - if root is reached the node is kept
 * - if root is not reachable, the node (along with any ancestors walked) are marked for removal
 * - marked node are removed
 *
 * You can test this code in isolation here: https://codesandbox.io/p/devbox/zen-thompson-mpczts?workspaceId=ws_LzLkA6dkgk8evgntyFbrse
 */
function removeDisconnectedSubtrees(treeNodes) {
    const nodes = { ...treeNodes };
    for (const key in nodes) {
        const node = nodes[key];
        if (!node) {
            // if we're here, it's likely the node was already removed
            continue;
        }
        const markedUids = new Set();
        let isRootAncestor = false;
        // check if node is decendant of the root node
        for (let ancestorUid = node.uid;;) {
            markedUids.add(ancestorUid);
            if (ancestorUid === pcbLayoutRootNodeId) {
                isRootAncestor = true;
                break;
            }
            const ancestor = nodes[ancestorUid];
            if (!ancestor) {
                // the ancestor may not exist if the node was already removed
                break;
            }
            ancestorUid = ancestor.parentUid;
        }
        if (!isRootAncestor) {
            // Remove the node and all its descendants
            for (const markedUid of markedUids) {
                delete nodes[markedUid];
            }
        }
    }
    return nodes;
}
/**
 * Filters out orphan nodes from the given tree of PCB nodes.
 */
export function filterOrphanNodes(treeNodes, logger) {
    const treeNodesNonOrphaned = removeDisconnectedSubtrees(treeNodes);
    // Rebuild childUids for each node in the output.
    for (const node of Object.values(treeNodesNonOrphaned)) {
        node.childUids = node.childUids.filter((childUid) => {
            // Do not remove virtual contain nodes which are unlikely to be present in
            // the treeNodesNonOrphaned, but are still required children uids to maintain
            // required tree structure.
            if (childUid.endsWith(".shapes") || childUid.endsWith(".mounts")) {
                return true;
            }
            return childUid in treeNodesNonOrphaned;
        });
    }
    logger?.captureError(new Error(`Nodes are orphaned and are being discarded from baking`));
    return treeNodesNonOrphaned;
}
//# sourceMappingURL=filterOrphanNodes.js.map